import { useEffect, useMemo, useState } from 'react';
import legacyUapiService from '../../services/LegacyUapiService';
import { routeGrantKey } from '../IntegrationDesigner';

// WHAT EACH ROUTE'S CALLER MAY ACTUALLY REACH, shown beside its credential for information — for the pipeline designer's
// boards (the template's on IntegrationForm, an implementation's on ImplementationBoard).
//
// Asked PER ROUTE — its credential and the backend binding it lands on — and answered by the same resolver the gateway
// enforces with (/opt/core/uapi-entitlement): the binding's override, else the access profile (credential in PROFILE
// mode) or the credential's own grants (DIRECT). Reading only the credential's own list (uapi_credential_module) showed
// /oauth for a credential whose access profile grants /rsv, because in PROFILE mode the gateway ignores that list.
//
// Keyed by routeGrantKey, which is what IntegrationDesigner's `routeGrants` prop reads. A failure is silent: this is
// context, and a red error over a working board would report the wrong thing.
export default function useRouteGrants(routes) {
  const pairs = useMemo(() => {
    const out = new Map();
    for (const r of routes || []) {
      if (r && r.credential_id) out.set(routeGrantKey(r), { credentialId: r.credential_id, accessId: r.uapi_access_id || null });
    }
    return out;
  }, [routes]);
  const pairsKey = [...pairs.keys()].sort().join(',');
  const [grants, setGrants] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!pairs.size) { setGrants({}); return undefined; }
    Promise.all([...pairs.entries()].map(([key, p]) =>
      legacyUapiService.getEffectiveModules(p.credentialId, p.accessId)
        .then(res => {
          const e = res.data?.effective;
          return [key, e ? {
            source: e.source, profileName: e.profileName || null,
            modules: (e.modules || []).map(m => ({ uapi_route_id: m.id, path: m.path, partial: !!m.partial })),
          } : null];
        })
        .catch(() => [key, null])))
      .then(entries => { if (!cancelled) setGrants(Object.fromEntries(entries.filter(([, v]) => v))); });
    return () => { cancelled = true; };
    // pairsKey, not pairs: a re-render with the same routes must not refetch every grant.
    // eslint-disable-next-line
  }, [pairsKey]);
  return grants;
}
